import { CircularProgress, Grid2 as Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ProductDetailRow } from "../ProductDetailRow";
import { MutationsSection } from "../MutationsSection";
import { useProductDetails } from "../../hooks/useProductDetails";
import { formatDate, formatDateTime } from "../../utils/dateFormat";

interface Props {
  productId: string;
}

export const CropDetailsTab: React.FC<Props> = ({ productId }) => {
  const { t } = useTranslation();
  const { data: product, isLoading, isError } = useProductDetails(productId);

  if (isLoading) {
    return <CircularProgress aria-label={t("product.loading")} />;
  }

  if (isError || !product) {
    return <Typography p={2}>{t("product.error")}</Typography>;
  }

  const crop = product.crop;

  return (
    <Grid container spacing={2} p={0}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            mb: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          {t("crop.detailDrawer.section.basicInfo")}
        </Typography>
        <Grid container spacing={1}>
          <ProductDetailRow
            label={t("crop.detailDrawer.taxonomicNumber")}
            value={crop?.taxonomicNumber?.toString()}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.registratorId")}
            value={crop?.registratorId?.toString()}
          />
        </Grid>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            mb: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          {t("crop.detailDrawer.section.classification")}
        </Typography>
        <Grid container spacing={1}>
          <ProductDetailRow
            label={t("crop.detailDrawer.genus")}
            value={crop?.genus?.name}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.genusCode")}
            value={crop?.genus?.code?.toString()}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.species")}
            value={crop?.species?.name}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.speciesCode")}
            value={crop?.species?.code?.toString()}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.cultivar")}
            value={crop?.cultivar?.name}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.cultivarCode")}
            value={crop?.cultivar?.code?.toString()}
          />
        </Grid>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            mb: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          {t("crop.detailDrawer.section.dates")}
        </Typography>
        <Grid container spacing={1}>
          <ProductDetailRow
            label={t("crop.detailDrawer.startDate")}
            value={formatDate(crop?.startDate)}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.endDate")}
            value={formatDate(crop?.endDate)}
          />
          <ProductDetailRow
            label={t("crop.detailDrawer.vbnChangeDateTime")}
            value={formatDateTime(crop?.vbnChangeDateTime)}
          />
        </Grid>
      </Grid>

      <MutationsSection
        createdBy={crop?.createdBy}
        createdDate={crop?.created}
        lastModifiedBy={crop?.lastModifiedBy}
        lastModifiedDate={crop?.lastModified}
      />
    </Grid>
  );
};
